import DoctorDetail from "issara-sdk/apis/DoctorDetail_core";
import DoctorList from "issara-sdk/apis/DoctorList_core";

import EmployeeDetailAPI from "issara-sdk/apis/EmployeeDetailAPI_users";
import EmployeeFamilyDetailAPI from "issara-sdk/apis/EmployeeFamilyDetailAPI_users";
import EmployeeFamilyListAPI from "issara-sdk/apis/EmployeeFamilyListAPI_users";
import EmployeeListAPI from "issara-sdk/apis/EmployeeListAPI_users";
import UserDetailAPIView from "issara-sdk/apis/UserDetailAPIView_users";

import UserDetailAPIViewM from "issara-sdk/apis/UserDetailAPIViewM";

import DoctorProviderView from "issara-sdk/apis/DoctorProviderView_apps_QUE";

import AffiliateSerializer from "issara-sdk/types/AffiliateSerializer_users";
import EmployeeFamilySerializer from "issara-sdk/types/EmployeeFamilySerializer_users";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import UserDetailSerializer from "issara-sdk/types/UserDetailSerializer_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapData,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";
import { getDivisionHierarchy } from "react-lib/apps/HISV3/common/sequence/AppInitialize";

import {
  AvailableAffiliateParamsType,
  flattenDivisionTree,
  groupDivisionOrganization,
  searchDivisionsAsTree,
} from "../HRMInterface";

import { adToBeWithSetFormat, beToAd, convertAdBeDateToMoment } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  AFFILIATE_SEARCH: "AFFILIATE_SEARCH",
  SAVE: "SAVE",
} as const;

export const SEQUENCE_NAME = "EmployeeInfo" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeeInfoSequence: Partial<
    EmployeeInfoType & {
      sequenceIndex: "Action" | "START" | null;
      affiliateItems: AffiliateSerializer[];
      divisionOrganizations: Record<string, any>[];
    }
  > | null;
  masterOptions: MasterOptions;
}>;

type EmployeeInfoType = {
  // Basic Employee Information
  basic: BasicInfoType;
  // Contact Information
  contact_address: BasicInfoType["contact_address"];
  // Employment Information
  employment: EmploymentType;
  // Address on ID Card
  id_card_address: BasicInfoType["id_card_address"];
  // Personal Information
  personal: PersonalInfoType;

  user: UserDetailType;
  username: string;
};

export type PickedState = PickMainState<
  "buttonLoadCheck" | "changePassword" | "errorMessage" | "selectedEmployee" | "successMessage"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type MasterData = MasterMapData<typeof Masters>;

type BasicInfoType = Omit<EmployeeSerializer, "organization" | "position" | "status"> & {
  citizen_type: "F" | "T";
  contact_address:
    | (EmployeeAddressType & {
        emergency_contact_name?: string;
        emergency_contact_tel?: string;
        use_id_card_address_as_contact?: boolean;
      })
    | null;
  current_status?: string;
  emergency_contact_name?: string;
  emergency_contact_tel?: string;
  id_card_address: EmployeeAddressType | null;
  managed_division?: number;
  organization?: number;
  position?: number;
  status: any;
  use_id_card_address_as_contact?: boolean;
};

type EmploymentType = Partial<{
  affiliate_name: string;
  certificate_no: string;
  doctor_code: string;
  doctor_id: number | null;
  isDoctor: boolean;
  isManager: boolean;
  isNoneDoctor: boolean;
  languages: number[];
  position?: number;
  specialties: number[];
  status: "ACTIVE" | "INACTIVE";
}> &
  Pick<
    EmployeeSerializer,
    "affiliate" | "employment_type" | "flag_end_date" | "flag_start_date" | "licence"
  >;

type UserDetailType = Omit<UserDetailSerializer, "image"> & { image?: File | string | null };

type PersonalInfoType = Partial<{
  family: EmployeeFamilyType[];
  marital_status: string | null;
  spouse_name: string;
}>;

type EmployeeFamilyType = EmployeeFamilySerializer & {
  active: boolean;
  index?: number;
  type: "CHILD" | "SPOUSE";
};

type EmployeeAddressType = Partial<{
  city: any;
  country: any;
  district: any;
  name: string;
  no: string;
  province: any;
  road: string;
  street: string;
  substreet: string;
  tel_mobile: string;
  zipcode: string;
}>;

type InitParamsType = {
  employeeId?: number;
  userId?: number;
};

export type RequiredFieldType = Partial<
  Record<
    | "birth_date"
    | "citizen_passport"
    | "code"
    | "contact_address"
    | "email"
    | "first_name"
    | "first_name_en"
    | "last_name"
    | "last_name_en"
    | "licence"
    | "nationality"
    | "passport_no"
    | "phone_no"
    | "pre_name"
    | "pre_name_en"
    | "religion",
    string
  >
>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  allDivisionList?: any[];
  device?: number;
  division?: number;
  allDivisionGroup?: any[];
  masterData?: MasterMapData<typeof Masters>;
};

export type SeqState = BaseSeqState<SequenceName> & InitParamsType;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.AFFILIATE_SEARCH]: AvailableAffiliateParamsType;
  [ACTIONS.INIT]: InitParamsType;
  [ACTIONS.SAVE]: Record<string, unknown>;
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeInfoSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["nationality", {}],
  ["religion", {}],
  ["prenameTh", {}],
  ["prenameEn", {}],
  ["employeeMaritalStatus", {}],
  ["positionAll", {}],
  ["employmentTypeName", {}],
  ["specialty", {}],
  ["currentEmpStatus", {}],
  ["province", {}],
  ["district", {}],
  ["city", {}],
] as const;

export const IDENTITY_DOCUMENT_OPTIONS = [
  { key: "T", text: "ID card Number", value: "T" },
  { key: "F", text: "Passport Number", value: "F" },
];

export const STATUS_OPTIONS = [
  { key: 1, text: "Active", value: "ACTIVE" },
  { key: 2, text: "Inactive", value: "INACTIVE" },
];

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    { EmployeeInfoSequence: { ...state.EmployeeInfoSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.AFFILIATE_SEARCH]: HandleParentSearch,
    [ACTIONS.SAVE]: HandleSave,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler<Params<Actions["INIT"]>> = async (controller, params) => {
  const state = controller.getState();

  const divisions = groupDivisionOrganization(controller.data.allDivisionList ?? []);
  const flatted = flattenDivisionTree(divisions);

  const detail = await GetUserEmployeeDetail(controller, params);

  controller.setState({
    EmployeeInfoSequence: {
      ...state.EmployeeInfoSequence,
      ...detail,
      affiliateItems: divisions,
      divisionOrganizations: flatted,
    },
    selectedEmployee: detail.basic as EmployeeSerializer,
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]> & { sendNewPasswordEmail?: boolean }> = async (
  controller,
  params
) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const seq = state.EmployeeInfoSequence;

  if (!seq?.basic || !seq.employment) {
    return;
  }

  // - CreateUpdateEmployee
  const sendEmail = (params as any)?.params?.sendNewPasswordEmail ?? false;
  const [employeeRes, employeeError] = await CreateUpdateEmployee(controller, {
    sendNewPasswordEmail: sendEmail,
  });
  const userId = employeeRes?.user_employee?.[0].id;
  const userName = employeeRes?.user_employee?.[0].username;
  const employeeId = employeeRes?.id;

  if (!employeeId) {
    SetErrorMessage(controller, { ...params, error: employeeError, errorKey: BTN_ACTS.SAVE });

    return;
  }

  if (!userId) {
    SetErrorMessage(controller, { ...params, error: "User ID not found", errorKey: BTN_ACTS.SAVE });

    return;
  }

  state = controller.getState();

  controller.setState({
    EmployeeInfoSequence: {
      ...state.EmployeeInfoSequence,
      basic: { ...seq.basic, id: employeeId, birth_date: employeeRes.birth_date || "" },
      username: userName,
    },
    selectedEmployee: employeeRes,
  });

  // - PatchUserDetail
  const [userRes, userError] = await PatchUserDetail(controller, {
    data: { ...employeeRes, image: seq.user?.image },
    userId,
  });

  if (userError) {
    SetErrorMessage(controller, { ...params, error: userError });

    return;
  }

  state = controller.getState();

  controller.setState({ EmployeeInfoSequence: { ...state.EmployeeInfoSequence, user: userRes } });

  // - CreateUpdateDoctor
  const [doctorRes, doctorError] = await CreateUpdateDoctor(controller, { userId });

  if (doctorError) {
    SetErrorMessage(controller, { ...params, error: doctorError });

    return;
  }

  state = controller.getState();

  controller.setState({
    EmployeeInfoSequence: {
      ...state.EmployeeInfoSequence,
      employment: { ...seq.employment, doctor_id: doctorRes?.id || null },
    },
  });

  // - CreateUpdateFamily
  const [, familyError] = await CreateUpdateFamily(controller, { employeeId });

  if (familyError) {
    SetErrorMessage(controller, { ...params, error: familyError });

    return;
  }

  // - GetEmployeeFamilyList
  const family = await GetEmployeeFamilyList(controller, { employeeId });

  state = controller.getState();

  controller.setState({
    EmployeeInfoSequence: {
      ...state.EmployeeInfoSequence,
      personal: { ...seq.personal, family },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
    selectedEmployee: employeeRes,
  });
};

const HandleParentSearch: Handler<Params<Actions["AFFILIATE_SEARCH"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const seq = state.EmployeeInfoSequence ?? {};
  const employment = seq.employment;

  const items = searchDivisionsAsTree(seq.divisionOrganizations ?? [], params.search);

  if ("search" in params && employment) {
    controller.setState({
      EmployeeInfoSequence: {
        ...seq,
        affiliateItems: items,
        employment: { ...employment, affiliate: null, affiliate_name: "" },
      },
    });

    return;
  }

  controller.setState({ EmployeeInfoSequence: { ...seq, affiliateItems: items } });
};

/** ====================================================  */
/**                         APIs                          */
/** ====================================================  */
const PatchUserDetail: Handler<
  { data: UserDetailType; userId: number },
  Promise<[UserDetailSerializer, unknown]>
> = async (controller, params) => {
  const data = params.data;

  if (data.image instanceof File) {
    await UserDetailAPIViewM.patch({
      apiToken: controller.apiToken,
      data: {
        image: data.image,
      } as any,
      pk: params.userId,
      extra: { division: controller.data.division },
    });
  }

  delete data.image;

  // update user detail
  return UserDetailAPIView.patch({
    apiToken: controller.apiToken,
    data: data as UserDetailSerializer,
    pk: params.userId,
    extra: { division: controller.data.division },
  }) as Promise<[UserDetailSerializer, unknown]>;
};

const CreateUpdateEmployee: Handler<
  { sendNewPasswordEmail?: boolean },
  Promise<[EmployeeSerializer | null, unknown]>
> = async (controller, options = {}) => {
  const state = controller.getState();

  const seq = state.EmployeeInfoSequence;
  const basic = seq?.basic;
  const personal = seq?.personal;
  const employment = seq?.employment;

  if (!basic || !employment) {
    return [null, null];
  }

  const prenameEn = controller.data.masterData?.prenameEn;

  const foundPrename = prenameEn?.find((item) => item.id === basic.pre_name_en);

  const api = basic.id ? EmployeeDetailAPI.patch : EmployeeListAPI.create;

  const contactAddress = Object.values(seq.contact_address ?? {}).some(Boolean)
    ? {
        ...seq.contact_address,
        edited: undefined,
      }
    : null;

  const idCardAddress = Object.values(seq.id_card_address ?? {}).some(Boolean)
    ? {
        ...seq.id_card_address,
        edited: undefined,
      }
    : null;

  const data: BasicInfoType & { send_new_password_email?: boolean } = {
    ...basic,
    auto_create_user: true,
    birth_date: convertAdBeDateToMoment(basic.birth_date)?.format("YYYY-MM-DD") || "",
    gender: foundPrename?.gender,
    organization: 1,
    ...personal,
    ...employment,
    contact_address: contactAddress,
    emergency_contact_name: seq.contact_address?.emergency_contact_name,
    emergency_contact_tel: seq.contact_address?.emergency_contact_tel,
    flag_end_date: beToAd(employment.flag_end_date)?.format("YYYY-MM-DD") || "",
    flag_start_date: beToAd(employment.flag_start_date)?.format("YYYY-MM-DD") || "",
    id_card_address: idCardAddress,
    marital_status: personal?.marital_status || undefined,
    status: employment.status as any,
    use_id_card_address_as_contact: seq.contact_address?.use_id_card_address_as_contact ?? false,
    send_new_password_email: options.sendNewPasswordEmail ?? false,
  };

  const result = (await api({
    apiToken: controller.apiToken,
    data,
    employee_id: basic.id,
  })) as [EmployeeSerializer | null, unknown];

  if (typeof result[0]?.affiliate === "number") {
    const divisions = getDivisionHierarchy(
      result[0].affiliate,
      controller.data.allDivisionGroup ?? []
    );

    result[0].affiliate = [...divisions].reverse();
  }

  return result;
};

const CreateUpdateFamily: Handler<{ employeeId: number }, Promise<[unknown, unknown]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const family = state.EmployeeInfoSequence?.personal?.family ?? [];

  const filteredFamily = family
    .filter((item) => {
      // ถ้ามี id อยู่แล้ว
      if (item.id) {
        return true;
      }

      // ถ้าไม่มี id ดูข้อมูลอย่างน้อยหนึ่งอย่าง
      return item.active && (!!item.name || !!item.cid || !!item.birth_date);
    })
    .map((item) => ({
      ...item,
      birth_date: item.birth_date ? beToAd(item.birth_date)?.format("YYYY-MM-DD") : null,
    }));

  const addItems = filteredFamily.filter((item) => !item.id);
  const deleteItems = filteredFamily.filter((item) => !item.active);
  const updateItems = filteredFamily.filter((item) => item.id && item.active);

  const payload = {
    ADD: {
      api: EmployeeFamilyListAPI.create,
      data: addItems,
    },
    DELETE: {
      api: EmployeeFamilyDetailAPI.delete,
      data: deleteItems,
    },
    UPDATE: {
      api: EmployeeFamilyDetailAPI.update,
      data: updateItems,
    },
  };

  const promiseArr = Object.values(payload).flatMap((value) =>
    value.data.map(
      async (item) =>
        value.api({
          apiToken: controller.apiToken,
          data: item as any,
          employee_id: params.employeeId,
          pk: item.id,
        }) as Promise<[unknown, unknown]>
    )
  );

  const response = await Promise.all(promiseArr);

  const isError = response.some((item: any) => item[1]);

  return isError
    ? [null, response.map((item) => item[1])]
    : [response.map((item) => item[0]), null];
};

const GetUserEmployeeDetail: Handler<InitParamsType, Promise<EmployeeInfoType>> = async (
  controller,
  params
) => {
  let user: UserDetailType = {};
  let basic: BasicInfoType = {
    birth_date: "",
    citizen_type: "T",
    code: "",
    contact_address: null,
    email: "",
    employment_type: "FULL_TIME",
    first_name_en: "",
    flag_end_date: "",
    flag_start_date: "",
    id_card_address: null,
    last_name_en: "",
    status: "ACTIVE",
  };

  let doctor: Record<string, any> = {};

  const userId = params.userId;

  if (userId) {
    const [[basicRes], userRes] = await Promise.all([
      EmployeeDetailAPI.retrieve({
        apiToken: controller.apiToken,
        employee_id: params.employeeId,
      }),
      GetUserDetail(controller, { userId }),
    ]);

    if (userRes.doctor) {
      [doctor] = await DoctorDetail.retrieve({
        apiToken: controller.apiToken,
        pk: userRes.doctor,
        extra: { division: controller.data.division },
      });
    }

    basic = basicRes || {};
    user = userRes;
  }

  const family = await GetEmployeeFamilyList(controller, {
    employeeId: params.employeeId,
  });

  const affiliate: Record<string, any>[] = basic.affiliate || [];

  return {
    basic: {
      ...basic,
      citizen_type: basic.passport_no ? "F" : "T",
    },
    contact_address: {
      ...basic.contact_address,
      emergency_contact_name: basic.emergency_contact_name,
      emergency_contact_tel: basic.emergency_contact_tel,
      use_id_card_address_as_contact: basic.use_id_card_address_as_contact,
    },
    employment: {
      affiliate: affiliate[0]?.id,
      affiliate_name: affiliate.map((item) => item.name as string).join(", "),
      certificate_no: doctor.certificate_no,
      current_status: basic.current_status,
      doctor_code: doctor.code,
      doctor_id: doctor.id,
      employment_type: basic.employment_type,
      flag_end_date: adToBeWithSetFormat(basic.flag_end_date),
      flag_start_date: adToBeWithSetFormat(basic.flag_start_date),
      inactive_reason: basic.inactive_reason,
      isDoctor: !!doctor.id,
      isManager: !!basic.managed_division,
      isNoneDoctor: !!basic.licence,
      languages: doctor.languages,
      licence: basic.licence,
      managed_division: basic.managed_division,
      position: basic.position,
      specialties: doctor.specialties,
      status: basic.status,
    },
    id_card_address: basic.id_card_address ?? {},
    personal: {
      family,
      marital_status: basic.marital_status,
    },
    user,
    username: basic.user_employee?.[0]?.username || "",
  };
};

const GetEmployeeFamilyList: Handler<
  { employeeId?: number },
  Promise<EmployeeFamilyType[]>
> = async (controller, params) => {
  let family: EmployeeFamilyType[] = [];

  if (params.employeeId) {
    const [familyRes] = await EmployeeFamilyListAPI.list({
      apiToken: controller.apiToken,
      employee_id: params.employeeId,
    });

    const familyItems: EmployeeFamilyType[] = familyRes?.items || [];

    family = familyItems.map((item) => ({
      ...item,
      active: true,
      birth_date: adToBeWithSetFormat(item.birth_date || ""),
    }));
  }

  const foundChild = family.find((item) => item.type === "CHILD");

  if (!foundChild) {
    family.unshift({ active: true, birth_date: "", cid: "", name: "", type: "CHILD" });
  }

  return family;
};

const CreateUpdateDoctor: Handler<{ userId: number }, Promise<[any, unknown]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const basic = state.EmployeeInfoSequence?.basic ?? {};
  const employment = state.EmployeeInfoSequence?.employment;

  if (!employment?.isDoctor) {
    return [null, null];
  }

  const data = {
    ...basic,
    certificate_no: employment.certificate_no,
    code: employment.doctor_code,
    languages: employment.languages,
    specialties: employment.specialties ?? [],
    user: params.userId,
  };

  if (!employment.doctor_id) {
    data.languages = [1];
  }

  const api = employment.doctor_id ? DoctorDetail.patch : DoctorList.create;

  const result = await api({
    apiToken: controller.apiToken,
    data: data as any,
    pk: employment.doctor_id,
  });

  await CreateDoctorProvider(controller, { doctor: result[0]?.id });

  return result as Promise<[any, unknown]>;
};

const CreateDoctorProvider: Handler<{ doctor: number }, Promise<any[]>> = async (
  controller,
  params
) => {
  if (!params.doctor) {
    return [];
  }

  return DoctorProviderView.create({
    apiToken: controller.apiToken,
    data: { doctor: params.doctor },
  }) as Promise<any[]>;
};

const GetUserDetail: Handler<{ userId: number }, Promise<UserDetailType>> = async (
  controller,
  params
) => {
  const [result] = await UserDetailAPIView.retrieve({
    apiToken: controller.apiToken,
    pk: params.userId,
    extra: { division: controller.data.division },
  });

  const data = result || {};

  return data as UserDetailType;
};
